import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';

const navLinks = [
  { key: 'nav.services', path: '/services' },
  { key: 'nav.expertise', path: '/expertise' },
  { key: 'nav.about', path: '/about' },
  { key: 'nav.testimonials', path: '/testimonials' },
  { key: 'nav.bookSession', path: '/booking' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-[72px]">
          <Link
            to="/"
            className="font-heading text-xl md:text-2xl font-bold whitespace-nowrap cursor-pointer"
          >
            <span className={scrolled || !isHome ? 'text-foreground-950' : 'text-white'}>
              Mentored<span className="text-accent-500">by Julen</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const label = t(link.key);
              const isCta = link.key === 'nav.bookSession';
              const isActive = location.pathname === link.path;
              const textClass = scrolled || !isHome
                ? (isActive ? 'text-foreground-950 bg-background-100' : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100')
                : (isActive ? 'text-white bg-white/15' : 'text-white/80 hover:text-white hover:bg-white/10');

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    isCta
                      ? 'bg-primary-500 text-white hover:bg-primary-600'
                      : textClass
                  }`}
                >
                  {label}
                </Link>
              );
            })}
            <div className={`ml-2 pl-2 ${scrolled || !isHome ? 'border-l border-background-200/70' : 'border-l border-white/20'}`}>
              <LanguageSwitcher />
            </div>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              type="button"
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                scrolled || !isHome ? 'text-foreground-700 hover:bg-background-100' : 'text-white hover:bg-white/10'
              }`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={t('nav.toggleMenu')}
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) => {
              const label = t(link.key);
              const isCta = link.key === 'nav.bookSession';
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                    isCta
                      ? 'bg-primary-500 text-white hover:bg-primary-600'
                      : isActive
                        ? 'bg-background-100 text-foreground-950'
                        : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  {label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}